import React, { useId, useState } from 'react';
import { Button, Input, type InputRef } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import css from './DesignPresetsEditor.scss';

const focusName = (input: InputRef | null) => input?.focus({ preventScroll: true, cursor: 'all' });

export default function DesignPresetFolderName({
	name,
	path,
	disabled,
	onRename,
}: {
	name: string;
	path: string[];
	disabled?: boolean;
	onRename: (name: string) => void;
}) {
	const [draft, setDraft] = useState<string>();
	const errorId = useId();
	const invalid = draft !== undefined && (!draft.trim() || draft.includes('/'));
	const save = () => {
		if (draft === undefined || invalid || disabled) return;
		const next = draft.trim();
		setDraft(undefined);
		if (next !== name) onRename(next);
	};
	return draft === undefined ? (
		<span className={css.folderName}>
			<span title={path.join(' / ')}>{name}</span>
			<Button
				type="text"
				size="small"
				className={css.folderAction}
				icon={<EditOutlined />}
				aria-label={`Rename folder ${path.join('/')}`}
				disabled={disabled}
				onClick={event => {
					event.stopPropagation();
					setDraft(name);
				}}
			/>
		</span>
	) : (
		<span className={css.folderNameField}>
			<Input
				ref={focusName}
				size="small"
				aria-label={`Folder name: ${path.join('/')}`}
				aria-invalid={invalid}
				aria-describedby={invalid ? errorId : undefined}
				status={invalid ? 'error' : undefined}
				value={draft}
				disabled={disabled}
				onChange={event => setDraft(event.target.value)}
				onClick={event => event.stopPropagation()}
				onBlur={save}
				onKeyDown={event => {
					event.stopPropagation();
					if (event.key === 'Enter') {
						event.preventDefault();
						save();
					} else if (event.key === 'Escape') {
						event.preventDefault();
						setDraft(undefined);
					}
				}}
			/>
			{invalid && <span id={errorId}>Enter a single folder name without /.</span>}
		</span>
	);
}
